// Browser detection utilities for Chrome-specific debugging

export const getBrowserInfo = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { name: 'unknown', version: 'unknown', isChrome: false, isSafari: false };
  }

  const userAgent = navigator.userAgent;
  const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
  const isSafari = /Safari/.test(userAgent) && /Apple Computer/.test(navigator.vendor);
  const isEdge = /Edg/.test(userAgent);
  const isFirefox = /Firefox/.test(userAgent);

  let name = 'unknown';
  let version = 'unknown';

  if (isEdge) {
    name = 'Edge';
    const match = userAgent.match(/Edg\/(\d+)/);
    version = match ? match[1] : 'unknown';
  } else if (isChrome && !isEdge) {
    name = 'Chrome';
    const match = userAgent.match(/Chrome\/(\d+)/);
    version = match ? match[1] : 'unknown';
  } else if (isSafari) {
    name = 'Safari';
    const match = userAgent.match(/Version\/(\d+)/);
    version = match ? match[1] : 'unknown';
  } else if (isFirefox) {
    name = 'Firefox';
    const match = userAgent.match(/Firefox\/(\d+)/);
    version = match ? match[1] : 'unknown';
  }

  return { name, version, isChrome, isSafari, isEdge, isFirefox };
};

export const isLocalStorageAvailable = (): boolean => {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (error) {
    return false;
  }
};

export const isIncognitoMode = async (): Promise<boolean> => {
  if (typeof window === 'undefined') return false;

  // Chrome/Edge detection
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    try {
      const { usage, quota } = await navigator.storage.estimate();
      // In incognito mode, quota is typically much smaller
      return quota !== undefined && quota < 120000000; // Less than 120MB
    } catch {
      return false;
    }
  }

  // Safari detection
  try {
    localStorage.setItem('__test__', '1');
    localStorage.removeItem('__test__');
    return false;
  } catch {
    return true;
  }
};

export const logBrowserDiagnostics = async () => {
  const browser = getBrowserInfo();
  const storageAvailable = isLocalStorageAvailable();
  const incognito = await isIncognitoMode();

  console.group('🔍 Browser Diagnostics');
  console.log('Browser:', browser.name, browser.version);
  console.log('Chrome:', browser.isChrome ? '✓' : '✗');
  console.log('Safari:', browser.isSafari ? '✓' : '✗');
  console.log('LocalStorage Available:', storageAvailable ? '✓' : '✗');
  console.log('Incognito Mode:', incognito ? '✓' : '✗');
  console.log('User Agent:', navigator.userAgent);
  console.log('Cookie Enabled:', navigator.cookieEnabled ? '✓' : '✗');
  console.groupEnd();

  return { browser, storageAvailable, incognito };
};

export const getChromeSpecificIssues = async (): Promise<string[]> => {
  const issues: string[] = [];
  const browser = getBrowserInfo();

  if (browser.isChrome) {
    // Check for common Chrome issues
    if (!isLocalStorageAvailable()) {
      issues.push('LocalStorage is blocked. This may be due to privacy settings or incognito mode.');
    }

    const incognito = await isIncognitoMode();
    if (incognito) {
      issues.push('Incognito/Private mode detected. Some features may not work correctly.');
    }

    if (!navigator.cookieEnabled) {
      issues.push('Cookies are disabled. Authentication may not work properly.');
    }

    // Check for third-party cookie blocking
    try {
      const storageAccess = 'hasStorageAccess' in document;
      if (storageAccess) {
        const hasAccess = await (document as any).hasStorageAccess();
        if (!hasAccess) {
          issues.push('Third-party storage access is blocked. This may affect authentication.');
        }
      }
    } catch {
      // Storage Access API not available
    }
  }

  return issues;
};
